/**
 * Shared session-termination helpers for the staff-facing API services and
 * the AppBar's logout menu item.
 *
 * There are two distinct ways a session ends, and they deliberately behave
 * differently:
 *
 *  - The server says it is over (401 from any admin/staff endpoint). That
 *    path uses `window.location.href` — a hard redirect — rather than a
 *    router `navigate`, so nothing in memory (component state, in-flight
 *    requests) survives into whatever page loads next.
 *  - The operator asks it to end (the Logout menu item). That path is an
 *    ordinary SPA navigate; there is no untrusted state to guarantee is torn
 *    down before the next render.
 *
 * Both clear the same five localStorage keys, so that list lives here once.
 */

export const SESSION_STORAGE_KEYS = [
  'accessToken',
  'refreshToken',
  'userId',
  'username',
  'userPermissions',
];

/**
 * Thrown after a 401 has already cleared the session and kicked off the
 * redirect to /login. It exists so every caller's ordinary catch block has
 * something to catch — there is nothing left for the caller to do with it,
 * since the page is on its way out.
 */
export class SessionExpiredError extends Error {
  constructor(message = 'Session expired') {
    super(message);
    this.name = 'SessionExpiredError';
  }
}

const clearSessionStorage = () => {
  SESSION_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
};

export const clearSessionAndRedirect = () => {
  clearSessionStorage();
  window.location.href = '/login';
};

/**
 * Clears the session, kicks off the redirect, and always throws. Call this
 * from a 401 branch instead of just `clearSessionAndRedirect()` so a caller
 * can never accidentally proceed as though the response resolved normally.
 */
export const raiseSessionExpired = () => {
  clearSessionAndRedirect();
  throw new SessionExpiredError();
};

/**
 * Ends the session the operator explicitly asked to end, via the AppBar's
 * Logout menu item. See the module comment for why this does not reuse
 * clearSessionAndRedirect.
 */
export const performLogout = (navigate) => {
  clearSessionStorage();
  navigate('/login');
};
